import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ChevronRight, ArrowLeft, ShieldCheck, ShieldOff, Clock, RefreshCw, CheckCircle2, XCircle, AlertTriangle, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { HandoffsPanel } from '../components/HandoffsPanel';
import { ContentReportsPanel } from '../components/ContentReportsPanel';
import { UnsubscribesPanel } from '../components/UnsubscribesPanel';

function fmt(dt) { if (!dt) return '\u2014'; try { return new Date(dt).toLocaleString(); } catch { return String(dt); } }

function StatusBadge({ status }) {
  const map = {
    opted_in: { text: 'Opted in', class: 'bg-brand-green text-brand-cream' },
    opted_out: { text: 'Opted out', class: 'bg-brand-cream-dark text-brand-green' },
    dismissed: { text: 'Dismissed', class: 'bg-yellow-100 text-yellow-800' },
    unknown: { text: 'Not decided', class: 'bg-yellow-100 text-yellow-800' },
  };
  const s = map[status] || map.unknown;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${s.class}`}>{s.text}</span>;
}

function UserRow({ user, onView }) {
  return (
    <tr className="border-t border-brand-cream-dark">
      <td className="py-3 pr-4 text-sm text-brand-green break-all">{user.email}</td>
      <td className="py-3 pr-4 text-sm text-brand-green/70">{user.phone || '\u2014'}</td>
      <td className="py-3 pr-4"><StatusBadge status={user.sms_opt_in_status} /></td>
      <td className="py-3 pr-4 text-sm text-brand-green/70">{user.consent_history_count}</td>
      <td className="py-3 pr-4 text-sm text-brand-green/70">{fmt(user.created_at)}</td>
      <td className="py-3 pr-4 text-right">
        <button onClick={() => onView(user)} className="inline-flex items-center gap-1 text-sm text-brand-green font-semibold hover:underline">
          View <ChevronRight className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

function UsersTable({ users, onView }) {
  return (
    <div className="overflow-x-auto bg-white rounded-2xl border border-brand-cream-dark">
      <table className="min-w-full">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-brand-green/60">
            <th className="py-3 px-4">Email</th>
            <th className="py-3 pr-4">Phone</th>
            <th className="py-3 pr-4">Status</th>
            <th className="py-3 pr-4">Consent events</th>
            <th className="py-3 pr-4">Signed up</th>
            <th className="py-3 pr-4"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (<UserRow key={u.id} user={u} onView={onView} />))}
        </tbody>
      </table>
    </div>
  );
}

function ConsentEventCard({ ev }) {
  const Icon = ev.granted ? ShieldCheck : ShieldOff;
  return (
    <div className="p-5 rounded-xl bg-white border border-brand-cream-dark">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${ev.granted ? 'text-brand-green' : 'text-red-600'}`} />
        <span className="font-semibold text-brand-green">{ev.granted ? 'Consent granted' : 'Consent withdrawn'}</span>
        <span className="ml-auto text-xs text-brand-green/60 inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {fmt(ev.granted_at)}
        </span>
      </div>
      <p className="text-sm text-brand-green/80 italic mb-2">“{ev.text}”</p>
      <div className="text-xs text-brand-green/60 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
        <div><strong>Source:</strong> {ev.source || '\u2014'}</div>
        <div><strong>Version:</strong> {ev.text_version || '\u2014'}</div>
        <div><strong>IP:</strong> {ev.ip || '\u2014'}</div>
        <div className="break-all"><strong>User agent:</strong> {ev.user_agent || '\u2014'}</div>
        {ev.phone && <div className="sm:col-span-2"><strong>Phone at time:</strong> {ev.phone}</div>}
      </div>
    </div>
  );
}

function UserDetail({ userId, onBack }) {
  const { authAxios } = useAuth();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await authAxios.get(`/admin/users/${userId}/consent-history`);
        if (mounted) setDetail(data);
      } catch (err) {
        if (mounted) setError(err?.response?.data?.detail || 'Failed to load user.');
      }
    })();
    return () => { mounted = false; };
  }, [userId, authAxios]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!detail) return <p className="text-brand-green/60">Loading…</p>;
  const { user, consent_history: events } = detail;
  return (
    <div className="space-y-6">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-brand-green font-semibold hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to users
      </button>
      <div className="bg-white rounded-2xl border border-brand-cream-dark p-6">
        <h2 className="font-serif text-2xl text-brand-green">{user.email}</h2>
        <div className="mt-2 text-sm text-brand-green/70 flex flex-wrap gap-x-6 gap-y-1">
          <span><strong>Phone:</strong> {user.phone || '\u2014'}</span>
          <span><strong>Signed up:</strong> {fmt(user.created_at)}</span>
          <span><strong>Status:</strong> <StatusBadge status={user.sms_opt_in_status} /></span>
        </div>
      </div>
      <div>
        <h3 className="font-serif text-xl text-brand-green mb-3">Consent history ({events.length})</h3>
        {events.length === 0
          ? <p className="text-sm text-brand-green/60 bg-white border border-brand-cream-dark rounded-xl p-6">No consent events yet.</p>
          : <div className="space-y-3">{events.slice().reverse().map((ev, i) => (<ConsentEventCard key={ev.granted_at + '_' + i} ev={ev} />))}</div>}
      </div>
    </div>
  );
}

export default function Admin() {
  const { authAxios, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await authAxios.get('/admin/users');
        if (mounted) setUsers(data);
      } catch (err) {
        if (mounted) setError(err?.response?.data?.detail || 'Failed to load users.');
      }
    })();
    return () => { mounted = false; };
  }, [authAxios]);

  const onView = useCallback((u) => setSelectedId(u.id), []);
  const onBack = useCallback(() => setSelectedId(null), []);

  return (
    <div className="bg-brand-cream min-h-screen">
      <section className="bg-brand-green text-brand-cream py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Admin</span>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">User consent audit</h1>
          <p className="mt-3 text-brand-cream/75">Signed in as {user?.email}</p>
        </div>
      </section>
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          {error && <p className="mb-4 text-red-600">{error}</p>}
          {selectedId
            ? <UserDetail userId={selectedId} onBack={onBack} />
            : (
              <div className="space-y-8">
                <PayoutsPanel />
                <ReviewerSessionsPanel />
                <ContentReportsPanel />
                <HandoffsPanel />
                <UnsubscribesPanel />
                <UsersTable users={users} onView={onView} />
              </div>
            )}
        </div>
      </section>
    </div>
  );
}

function EventIcon({ event }) {
  if (event === 'login-success') return <CheckCircle2 className="w-4 h-4 text-brand-green" />;
  if (event === 'login-failed') return <XCircle className="w-4 h-4 text-red-600" />;
  return <AlertTriangle className="w-4 h-4 text-yellow-700" />;
}

function ReviewerSessionsPanel() {
  const { authAxios } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get('/admin/reviewer-sessions?limit=100');
      setSessions(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load reviewer sessions.');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { success: 0, failed: 0, http4xx: 0 };
    for (const s of sessions) {
      if (s.event === 'login-success') c.success += 1;
      else if (s.event === 'login-failed') c.failed += 1;
      else if (s.event === 'http-4xx') c.http4xx += 1;
    }
    return c;
  }, [sessions]);

  return (
    <div data-testid="reviewer-sessions-panel" className="bg-white rounded-2xl border border-brand-cream-dark p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-brand-green">Reviewer sessions</h2>
          <p className="text-sm text-brand-green/70 mt-1">
            Every login attempt and 4xx response for accounts with <code>role: "reviewer"</code>.
            Useful during Apple / Google review windows.
          </p>
        </div>
        <button onClick={load} data-testid="reviewer-sessions-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-brand-cream-dark text-brand-green hover:bg-brand-cream">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        <span className="px-2 py-1 rounded-full bg-brand-green/10 text-brand-green">Success: {counts.success}</span>
        <span className="px-2 py-1 rounded-full bg-red-50 text-red-700">Failed logins: {counts.failed}</span>
        <span className="px-2 py-1 rounded-full bg-yellow-50 text-yellow-800">4xx errors: {counts.http4xx}</span>
      </div>
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      {sessions.length === 0 && !loading && (
        <p className="text-sm text-brand-green/60 py-6 text-center">
          No reviewer activity yet. Events will appear here as the review team signs in.
        </p>
      )}
      {sessions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-brand-green/60">
                <th className="py-2 pr-4">When</th>
                <th className="py-2 pr-4">Event</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Path</th>
                <th className="py-2 pr-4">IP</th>
                <th className="py-2 pr-4">Detail</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} className="border-t border-brand-cream-dark align-top">
                  <td className="py-2 pr-4 text-brand-green whitespace-nowrap">{fmt(s.timestamp)}</td>
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-1.5 text-brand-green">
                      <EventIcon event={s.event} /> {s.event}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-brand-green/80">{s.status_code ?? '\u2014'}</td>
                  <td className="py-2 pr-4 text-brand-green/80 break-all">{s.path || '\u2014'}</td>
                  <td className="py-2 pr-4 text-brand-green/70 whitespace-nowrap">{s.ip || '\u2014'}</td>
                  <td className="py-2 pr-4 text-brand-green/70 break-all">{s.detail || '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// -------------------------------------------------- Referral payout queue
function fmtUsd(cents) {
  return (Number(cents || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function PayoutRow({ payout, onPaid }) {
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState('');
  const [notes, setNotes] = useState('');
  const { authAxios } = useAuth();
  const { toast } = useToast();

  const mark = useCallback(async () => {
    setBusy(true);
    try {
      await authAxios.post(`/admin/payouts/${payout.id}/mark-paid`,
                           { method: method || null, notes: notes || null });
      toast({ title: 'Payout marked paid' });
      onPaid(payout.id);
    } catch (err) {
      toast({ title: 'Could not mark paid', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  }, [authAxios, payout.id, method, notes, toast, onPaid]);

  const u = payout.user || {};
  return (
    <tr data-testid={`payout-row-${payout.id}`} className="border-b border-brand-cream last:border-0 align-top">
      <td className="p-3">
        <div className="font-medium text-brand-green">{u.name || u.email || payout.user_id}</div>
        <div className="text-xs text-brand-green/60">{u.email}</div>
        <div className="text-[10px] uppercase tracking-wide text-brand-gold mt-1">
          {u.subscription_provider || 'unknown'}
        </div>
      </td>
      <td className="p-3 font-semibold text-brand-green">{fmtUsd(payout.amount)}</td>
      <td className="p-3 text-xs text-brand-green/70">{fmt(payout.created_at)}</td>
      <td className="p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="method (cashapp / venmo…)" value={method}
                 onChange={(e) => setMethod(e.target.value)}
                 className="w-full sm:w-40 text-sm"
                 data-testid={`payout-method-${payout.id}`} />
          <Input placeholder="notes" value={notes}
                 onChange={(e) => setNotes(e.target.value)}
                 className="w-full sm:w-48 text-sm"
                 data-testid={`payout-notes-${payout.id}`} />
          <Button size="sm" onClick={mark} disabled={busy}
                  data-testid={`payout-mark-paid-${payout.id}`}>
            {busy ? 'Saving…' : 'Mark paid'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function PayoutsPanel() {
  const { authAxios } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get('/admin/payouts/pending');
      setPayouts(data.payouts || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load payouts.');
    } finally { setLoading(false); }
  }, [authAxios]);
  useEffect(() => { refresh(); }, [refresh]);

  const onPaid = useCallback((id) => {
    setPayouts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const total = useMemo(
    () => payouts.reduce((acc, p) => acc + Number(p.amount || 0), 0),
    [payouts],
  );

  return (
    <div className="bg-white rounded-2xl border border-brand-cream shadow-sm overflow-hidden" data-testid="admin-payouts-panel">
      <div className="p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-gold/10 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-brand-gold" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-green/60">Referral payouts</div>
            <div className="font-serif text-2xl text-brand-green">Pending manual payouts</div>
            <div className="text-xs text-brand-green/70">
              Apple-referrer bonuses (Apple doesn't allow mid-cycle discounts) — total pending {fmtUsd(total)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={refresh} data-testid="admin-payouts-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button size="sm" variant="outline" asChild data-testid="admin-payouts-csv">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/payouts/paid.csv`}
               target="_blank" rel="noreferrer">
              Download paid CSV
            </a>
          </Button>
        </div>
      </div>
      {error && <div className="px-5 pb-3 text-sm text-red-600">{error}</div>}
      {loading ? (
        <div className="p-5 text-brand-green/60 text-sm">Loading payouts…</div>
      ) : payouts.length === 0 ? (
        <div className="p-6 text-sm text-brand-green/60 text-center">
          Nothing pending — you're all caught up.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-brand-green/60 border-b border-brand-cream">
                <th className="p-3">Recipient</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Earned</th>
                <th className="p-3">Mark paid</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <PayoutRow key={p.id} payout={p} onPaid={onPaid} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
